'use client'

import type { OptionsType } from '@fukku/select'
import { FormProvider, useForm } from 'form-components'
import { SubmitButton } from 'form-components/components/SubmitButton/SubmitButton'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { FIELD_VALIDATION_ERROR } from 'refunds/constants/constants'
import { useBankDataForm } from 'refunds/hooks/useBankDataForm/useBankDataForm'
import { useSaveRefundData } from 'refunds/hooks/useSaveRefundData/useSaveRefundData'
import { useValidateBankDataOnServer } from 'refunds/hooks/useValidateBankDataOnServer/useValidateBankDataOnServer'
import type { BankDataInfo, RefundData } from 'refunds/types/refundData'
import { normalizeBankDataFormValues } from 'refunds/utils/normalizeBankDataFormValues'

import { MultiInput } from './components/MultiInput'

import styles from './BankDataForm.module.scss'

export interface FieldsData {
	[key: string]: OptionsType[] // Add more types whenever are needed
}
interface BankDataFormProps {
	readonly onError?: () => void
	readonly onSaveData?: (values: BankDataInfo) => void
	readonly onSubmit?: (values: BankDataInfo) => void
	readonly preloadData?: RefundData
	readonly validateWithoutSave?: boolean
	readonly overrideButtonText?: string
}

export function BankDataForm({
	onError,
	onSaveData,
	onSubmit,
	preloadData,
	validateWithoutSave = false,
	overrideButtonText,
}: BankDataFormProps) {
	const { t } = useLabels()
	const { fields, fieldsData } = useBankDataForm()
	const {
		country: { countryISO },
	} = useMasterData()
	const { validateBankDataOnServer } = useValidateBankDataOnServer({
		countryISO,
	})
	const { saveRefundData } = useSaveRefundData({ countryISO })

	const { bankDataInfo: preloadBankData } = preloadData?.bankData ?? {}
	const formMethods = useForm({
		...(preloadBankData && { defaultValues: preloadBankData }),
		mode: 'onTouched',
	})
	const { handleSubmit, setError } = formMethods

	if (!fields) {
		return <></>
	}

	const setFieldError = (
		fieldWithError: keyof BankDataInfo,
		errorType?: FIELD_VALIDATION_ERROR
	) => {
		const cField = fields.find((field) => field.field === fieldWithError)
		setError(fieldWithError, {
			type: 'custom',
			message:
				errorType === FIELD_VALIDATION_ERROR.BLOCKED &&
				cField?.blockedErrorLabel
					? cField.blockedErrorLabel
					: cField?.errorLabel,
		})
	}

	const validateDataOnServer = async (formValues: BankDataInfo) => {
		const { fieldWithError, errorType } =
			await validateBankDataOnServer(formValues)
		if (fieldWithError) {
			setFieldError(fieldWithError, errorType)
			return
		}
		onSubmit?.(formValues)
	}

	const saveData = async (
		formValues: BankDataInfo,
		prevRefundData: RefundData
	) => {
		const {
			isValid: isBankDataValid,
			fieldWithError,
			errorType,
		} = await saveRefundData({
			...prevRefundData,
			bankData: {
				bankDataInfo: formValues,
				isBlocked: false,
				selected: true,
				available: true,
			},
		})

		if (isBankDataValid) {
			onSaveData?.(formValues)
			return
		}
		if (fieldWithError) {
			setFieldError(fieldWithError, errorType)
			return
		}
		onError?.()
	}

	const handleOnSubmit = async (values: BankDataInfo): Promise<void> => {
		const normalizedFormValues = normalizeBankDataFormValues(fields, values)

		if (validateWithoutSave) {
			await validateDataOnServer(normalizedFormValues)
			return
		}

		if (preloadData) {
			await saveData(normalizedFormValues, preloadData)
		}
	}

	return (
		<FormProvider {...formMethods}>
			<form
				data-testid='refunds.bankData.form'
				className={styles.form}
				onSubmit={handleSubmit(handleOnSubmit)}
			>
				{fields.map((field) => (
					<MultiInput
						key={field.field}
						fieldConfig={field}
						{...(field.field in fieldsData && {
							fieldData: fieldsData[field.field],
						})}
					/>
				))}
				<SubmitButton
					id='refunds.bankData.form.submit'
					data-testid='refunds.bankData.form.submit'
					className={styles.button}
				>
					{overrideButtonText ?? t('form.bankDetails.add.button')}
				</SubmitButton>
			</form>
		</FormProvider>
	)
}
